import React, { useEffect, useId, useRef, useState } from 'react';
import {
    Alert,
    Button,
    CloseButton,
    Form,
    Modal,
    Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LoadError from '@/components/common/LoadError';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import { AttendanceRefusal } from '@/features/applications/student/programs/LaEligibility';
import { LA_ATTENDANCE_REFUSAL } from '@/features/applications/student/programs/laAnswers';
import YesNoQuestion from '@/features/applications/student/programs/YesNoQuestion';
import {
    PREVIEW_LOCK_NOTE_ID,
    usePreviewLock,
} from '@/shared/hooks/usePreviewLock';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import { positionKeyFacts } from '@/shared/utils/positionKeyFacts';
import { positionCourse, positionHeading } from '@/shared/utils/positionText';
import {
    commitmentLabel,
    courseQuestion,
} from '@/shared/utils/requirementAnswers';

const FALLBACK_ERROR = "Your application couldn't be sent. Try again.";

// Shown only when the student is at the limit or one below it, the only time
// the count changes what they do.
function limitLine(held, max) {
    if (max == null || held < max - 1) return null;
    return `You have accepted ${held} of ${max} ${max === 1 ? 'position' : 'positions'}. Applying to more is fine; the limit applies when you accept an offer.`;
}

/**
 * Confirms a quick apply before anything is sent: the position, when and
 * where it meets (one key-facts line), the hours a week, the commitment the student ticks, the
 * course question when the program asks it, and the student's own count of
 * accepted positions. `onConfirm({ commitmentConfirmed, courseCompleted })`
 * returns a promise; the parent closes the dialog when it resolves, and a
 * rejection's message is shown here with the dialog left open.
 */
function ApplyConfirmModal({
    show,
    position,
    hoursPerWeek,
    askCourse,
    requirementsFailed,
    onRetryRequirements,
    accepted,
    maxPositions,
    applicationLink,
    trainingText,
    resubmit,
    errorText,
    onConfirm,
    onCancel,
}) {
    const titleId = useId();
    const commitmentId = useId();
    const [confirmed, setConfirmed] = useState(false);
    const [courseCompleted, setCourseCompleted] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);
    const previewLocked = usePreviewLock();
    const errorRef = useRevealAlert(error);

    useEffect(() => {
        if (!show) return;
        setConfirmed(false);
        setCourseCompleted(null);
        setError(null);
    }, [show]);

    if (!position) return null;
    const heading = positionHeading(position);
    const limit = limitLine(accepted, maxPositions);
    const ready =
        !requirementsFailed &&
        confirmed &&
        (!askCourse || courseCompleted !== null);
    const missing = [
        ...(requirementsFailed ? ['retry the check above'] : []),
        ...(askCourse && courseCompleted === null
            ? ['answer the course question']
            : []),
        ...(confirmed ? [] : ['tick the box confirming you can commit']),
    ];
    const hintId = `${commitmentId}-hint`;

    const close = () => {
        if (!inFlight.current) onCancel();
    };

    const send = async () => {
        if (inFlight.current || !ready) return;
        inFlight.current = true;
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm({
                commitmentConfirmed: true,
                ...(askCourse ? { courseCompleted } : {}),
            });
        } catch (err) {
            setError(
                errorText
                    ? errorText(err)
                    : err?.response?.data?.message || FALLBACK_ERROR
            );
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={close}
            animation={false}
            fullscreen="md-down"
            aria-labelledby={titleId}
        >
            <Modal.Header>
                <Modal.Title id={titleId} as="h2" className="h5">
                    {resubmit ? 'Resubmit to' : 'Apply to'} {heading}
                </Modal.Title>
                <CloseButton
                    aria-label="Close"
                    onClick={close}
                    disabled={submitting}
                />
            </Modal.Header>
            <Modal.Body>
                <p className="mb-3">
                    Confirm each position you could accept. Selecting several
                    positions does not mean you will work all of them.
                </p>
                <p className="fw-semibold mb-1">
                    {positionKeyFacts(position, {
                        hoursPerWeek,
                        notePlace: true,
                    })}
                </p>
                {position.instructorName && (
                    <p className="mb-2 text-body-secondary">
                        {position.instructorName}
                    </p>
                )}
                {trainingText && <p className="small mb-3">{trainingText}</p>}

                {requirementsFailed && (
                    <LoadError
                        message="We could not check what this program asks of applicants, so you cannot submit yet."
                        onRetry={onRetryRequirements}
                    />
                )}

                {askCourse && (
                    <YesNoQuestion
                        id="apply-course-completed"
                        legend={courseQuestion(positionCourse(position))}
                        value={courseCompleted}
                        onChange={setCourseCompleted}
                        invalid={false}
                    />
                )}

                <Form.Check
                    type="checkbox"
                    id={commitmentId}
                    className="mb-3"
                    label={commitmentLabel(hoursPerWeek)}
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                />

                {limit && <p className="small mb-2">{limit}</p>}
                <p className="small mb-0">
                    Your LA application answers and resume go with this
                    application.{' '}
                    {applicationLink && (
                        <Link to={applicationLink}>
                            Review your application answers
                        </Link>
                    )}
                </p>

                {error && (
                    <Alert
                        variant="danger"
                        className="mt-3 mb-0"
                        ref={errorRef}
                        tabIndex={-1}
                    >
                        {error === LA_ATTENDANCE_REFUSAL ? (
                            <AttendanceRefusal />
                        ) : (
                            error
                        )}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer className="psh-apply-footer">
                <PreviewLockNote locked={previewLocked} />
                {!ready && (
                    <p
                        id={hintId}
                        className="small text-body-secondary w-100 mb-0"
                    >
                        To submit, {missing.join(' and ')}.
                    </p>
                )}
                <Button
                    variant="outline-primary"
                    onClick={close}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={send}
                    disabled={!ready || submitting || previewLocked}
                    aria-describedby={
                        previewLocked
                            ? PREVIEW_LOCK_NOTE_ID
                            : ready
                              ? undefined
                              : hintId
                    }
                >
                    {submitting && (
                        <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                            aria-hidden="true"
                        />
                    )}
                    {resubmit ? 'Resubmit' : 'Submit'} application
                    {submitting && (
                        <span className="visually-hidden"> in progress</span>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ApplyConfirmModal;
